'use client';

import React, { useState, useEffect } from 'react';
import CVRequestModal from '@/components/ui/CVRequestModal';

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export default function Hero() {
  const [greeting, setGreeting] = useState('');
  const [visible, setVisible] = useState(false);
  const [isCVModalOpen, setIsCVModalOpen] = useState(false);

  useEffect(() => {
    setGreeting(getGreeting());
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col justify-end bg-dark overflow-hidden"
    >
      {/* Background image + overlay */}
      <div className="absolute inset-0">
        <img
          src="/assets/images/WhatsApp_Image_2026-04-07_at_04.54.00-1777208109960.jpeg"
          alt="Ramson portrait"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Dark overlay to maintain readability */}
        <div
          className="absolute inset-0"
          style={{ background: 'rgba(20, 20, 24, 0.78)' }}
        />
        {/* Red accent shape */}
        <div
          className="absolute top-0 right-0 w-1/3 h-full opacity-10"
          style={{
            background:
              'radial-gradient(ellipse at top right, #7c3aed 0%, transparent 70%)',
          }}
        />
      </div>

      {/* Large background text */}
      <div
        className="absolute top-1/2 left-0 right-0 -translate-y-1/2 overflow-hidden pointer-events-none select-none"
        aria-hidden="true"
      >
        <div
          className="font-condensed font-bold uppercase text-light-warm"
          style={{
            fontSize: 'clamp(8rem, 20vw, 22rem)',
            lineHeight: '0.85',
            opacity: '0.03',
            letterSpacing: '-0.02em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          DIGITAL
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 px-5 md:px-16 lg:px-24 pb-20 md:pb-28">
        {/* Greeting line */}
        <div
          className={`transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="section-label" style={{ color: '#06b6d4' }}>
            {greeting &&
              `${greeting.split("'")[0]}'`}
          </span>
        </div>

        {/* Main heading */}
        <div
          className={`mt-2 transition-all duration-700 delay-300 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h1
            className="font-condensed font-bold uppercase text-light-warm"
            style={{
              fontSize: 'clamp(3.5rem, 9vw, 9rem)',
              lineHeight: '0.9',
              letterSpacing: '-0.02em',
            }}
          >
            <span style={{ fontSize: 'clamp(1.6rem, 4vw, 4rem)' }}>
              {greeting ? `${greeting}, I'm` : "I'm"}
            </span>
            <br />
            <span className="text-primary">Ramson</span>
          </h1>
        </div>

        {/* Subheading */}
        <div
          className={`mt-6 transition-all duration-700 delay-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p
            className="font-condensed font-medium uppercase tracking-widest text-secondary-dark"
            style={{ fontSize: 'clamp(0.9rem, 2vw, 1.2rem)' }}
          >
            Digital Marketing & Communications Specialist
          </p>
        </div>

        {/* Value statement */}
        <div
          className={`mt-6 max-w-xl transition-all duration-700 delay-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p
            className="font-serif text-secondary-dark"
            style={{ fontSize: 'clamp(1rem, 1.5vw, 1.2rem)', lineHeight: '1.6', fontStyle: 'italic' }}
          >
            &ldquo;I combine data, content, and strategy to drive measurable growth and clear communication.&rdquo;
          </p>
        </div>

        {/* CTA Buttons */}
        <div
          className={`mt-10 flex flex-wrap gap-3 transition-all duration-700 delay-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <button
            onClick={() => scrollToSection('projects')}
            className="btn-primary rounded-br-[5px] rounded-t-[5px] rounded-bl-[5px]"
          >
            View My Work
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            onClick={() => scrollToSection('contact')}
            className="rounded-br-[5px] rounded-t-[5px] rounded-bl-[5px] inline-flex items-center gap-2 px-5 py-3 font-bold uppercase tracking-wider text-sm border-2 border-white bg-white text-[#0ECAD4] transition-all duration-300 cursor-pointer hover:bg-transparent hover:text-white"
          >
            Contact Me
          </button>
          <button
            onClick={() => setIsCVModalOpen(true)}
            className="rounded-br-[5px] rounded-t-[5px] rounded-bl-[5px] inline-flex items-center gap-2 px-5 py-3 font-bold uppercase tracking-wider text-sm bg-[#0ECAD4] text-white border-2 border-[#0ECAD4] transition-all duration-300 cursor-pointer hover:bg-transparent hover:text-[#0ECAD4]"
          >
            Request My CV
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {/* Location badge */}
        <div
          className={`mt-8 flex items-center gap-2 transition-all duration-700 delay-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-primary">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor" />
          </svg>
          <span className="font-condensed text-xs tracking-widest uppercase text-secondary-dark">
            Based in Belgium
          </span>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 right-8 md:right-16 flex flex-col items-center gap-2">
        <span className="font-condensed text-xs tracking-widest uppercase text-secondary-dark opacity-60"
          style={{ writingMode: 'vertical-rl' }}>
          Scroll
        </span>
        <div className="w-px h-12 bg-secondary-dark opacity-30 relative overflow-hidden">
          <div
            className="absolute top-0 left-0 w-full bg-primary"
            style={{
              height: '40%',
              animation: 'scrollLine 2s ease-in-out infinite',
            }}
          />
        </div>
      </div>

      <CVRequestModal isOpen={isCVModalOpen} onClose={() => setIsCVModalOpen(false)} />

      <style jsx>{`
        @keyframes scrollLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(300%); }
        }
      `}</style>
    </section>
  );
}